import React from "react"
import PropTypes from "prop-types"
import Radium, { keyframes } from "higher-order-components/configured-radium/"

import { ErrorIcon, SearchIcon, CrossIcon, Select, InputLabel } from "components"

const renderIcon = (name, style) => {
  switch (name) {
    case "error":
      return <ErrorIcon style={style} />
    case "search":
      return <SearchIcon style={style} />
    case "cross":
      return <CrossIcon style={style} />
    default:
      return <CrossIcon style={style} />
  }
}

const HOURS = "01,02,03,04,05,06,07,08,09,10,11,12"
  .split(",")
  .map(name => ({ name, value: name.toLowerCase() }))
const MINUTES = "00,15,30,45".split(",").map(name => ({ name, value: name }))
const AMPM = "am,pm".split(",").map(name => ({ name: name.toUpperCase(), value: name }))

const themeDark = {
  label: valid => ({
    color: "ddd"
  }),
  input: valid => ({
    color: valid ? "#101010" : "#bf0000",
    borderColor: valid ? "#ddd" : "#bf0000"
  }),
  message: valid => ({
    color: valid ? "#101010" : "#bf0000"
  }),
  iconSvg: valid => ({
    fill: valid ? "#999" : "#bf0000"
  })
}

const themeLight = {
  label: valid => ({
    color: "#fff"
  }),
  input: valid => ({
    color: valid ? "#fff" : "#bf0000",
    borderColor: valid ? "#ddd" : "#bf0000"
  }),
  message: valid => ({
    color: valid ? "#fff" : "#bf0000"
  }),
  iconSvg: valid => ({
    fill: valid ? "#fff" : "#bf0000"
  })
}

const themes = {
  dark: themeDark,
  light: themeLight
}

/**
 * @param {Object} props - Component props
 * @param {String} props.label - Field label text
 * @param {String} props.value - Input value
 * @param {String} props.defaultValue - Default field value
 * @param {Number} props.maxLength - input maxLength property
 * @param {String} props.placeholder - Field placeholder text
 * @param {Boolean} props.disabled - disable input
 * @param {String} props.icon - Name of icon
 * @param {Boolean} props.isValid - validation status of input
 * @param {String} props.message - message displayed after input
 * @param {Boolean} props.required - input required
 * @param {Function} props.onChange - onChange callback of input
 * @param {Function} props.onIconClick - Callback for when icon is clicked.
 * @param {Function} props.styleName - change the theme of the component
 * @param {Array} props.gtmEvents - Array of gtm events
 * @returns {ReactElement} React Component
 */
const InputTime = ({
  label,
  value,
  defaultValue = "09:00 am",
  required,
  onChange,
  onIconClick,
  icon,
  message,
  isValid,
  styleName
}) => {
  const themeStyles = themes[styleName]

  const time = value || defaultValue

  let hhmmam = ""

  const lessThan9Add0 = number => {
    const newString = String(number)
    if (newString.length < 2) {
      return `0${newString}`
    }
    return newString
  }
  if (time.includes("/")) {
    hhmmam = [
      lessThan9Add0(String(time.split("/")[0])),
      lessThan9Add0(String(time.split("/")[1])),
      time.split("/")[2]
    ]
  } else {
    hhmmam = [
      lessThan9Add0(String(time.split(":")[0])),
      lessThan9Add0(String(time.split(":")[1].split(" ")[0])),
      time.split(" ")[1]
    ]
  }
  return (
    <div style={{ marginTop: "-1em", position: "relative" }} data-test="input-time">
      <div style={{ position: "relative" }}>
        {label && (
          <div style={{ position: "absolute", top: "70%", width: "100%" }}>
            <InputLabel text={label} dirty required={required} />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Select
            label=""
            options={HOURS}
            value={{ name: hhmmam[0], value: hhmmam[0] }}
            onChange={v => {
              onChange(`${v.value}:${hhmmam[1]} ${hhmmam[2]}`)
            }} />
          <span style={{ minWidth: "1em" }} />
          <Select
            label=""
            options={MINUTES}
            value={{ name: hhmmam[1], value: hhmmam[1] }}
            onChange={v => {
              onChange(`${hhmmam[0]}:${v.value} ${hhmmam[2]}`)
            }} />
          <span style={{ minWidth: "1em" }} />
          <Select
            label=""
            options={AMPM}
            value={{ name: hhmmam[2], value: hhmmam[2] && hhmmam[2].toLowerCase() }}
            onChange={v => {
              onChange(`${hhmmam[0]}:${hhmmam[1]} ${v.value}`)
            }} />
          <span />
        </div>
        {icon && (
          <div
            onClick={() => onIconClick({ icon })}
            style={[styles.iconWrapper, !!onIconClick && { cursor: "pointer" }]}>
            <div style={styles.icon}>
              {renderIcon(icon, {
                ...themeStyles.iconSvg(true),
                ...styles.baseSvg
              })}
            </div>
          </div>
        )}
        {!isValid && (
          <div style={[styles.iconWrapper, styles.fadeIn]}>
            <div style={styles.icon}>
              {renderIcon("error", {
                ...themeStyles.iconSvg(isValid),
                ...styles.baseSvg
              })}
            </div>
          </div>
        )}
      </div>
      {message && (
        <p style={[styles.message, themeStyles.message(isValid), styles.fadeIn]}>{message}</p>
      )}
    </div>
  )
}

InputTime.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  onIconClick: PropTypes.func,
  required: PropTypes.bool,
  defaultValue: PropTypes.string,
  icon: PropTypes.string,
  message: PropTypes.string,
  value: PropTypes.string,
  isValid: PropTypes.bool,
  styleName: PropTypes.oneOf(["light", "dark"])
}

InputTime.defaultProps = {
  value: "09:00",
  isValid: true,
  styleName: "dark"
}

const fadeInKeyframes = keyframes({
  from: {
    opacity: 0
  }
})

const styles = {
  baseSvg: {
    height: "100%",
    width: "100%"
  },
  fadeIn: {
    animation: "x .666s",
    animationName: fadeInKeyframes
  },
  message: {
    position: "absolute",
    fontSize: "75%",
    fontWeight: 200,
    top: "calc(100% + .5em)",
    margin: 0
  },
  iconWrapper: {
    position: "absolute",
    right: ".5em",
    top: "50%"
  },
  icon: {
    width: "1.4em",
    height: "1.4em",
    transform: "translateY(-50%)"
  },
  input: {
    /* disable box shadow for firefox red shadow on page load bug */
    boxShadow: "none",
    background: "none",
    boxSizing: "border-box",
    width: "100%",
    outline: "none",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    borderBottomStyle: "solid",
    borderBottomWidth: "2px"
  },
  iconPadding: {
    padding: "1.5em 2.4em .75em 0"
  }
}

export default Radium(InputTime)



// WEBPACK FOOTER //
// ./src/components/form-elements/input-time/index.jsx